import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Panel } from "@/components/terminal";

const ASSETS = ["BTC", "XRP", "XLM", "HBAR", "SOL", "ALGO", "ZBCN"];
const ARB_CLS = {
  OPPORTUNITY: "text-emerald-400 border-emerald-800",
  WATCH: "text-amber-300 border-amber-800",
  NONE: "text-slate-400 border-slate-700",
  BLOCKED: "text-red-400 border-red-900",
};

const fmt = (v, d = 2) => (v === null || v === undefined ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));
const usd = (v) => (v === null || v === undefined ? "—" : `$${fmt(v)}`);

export default function VenueIntelligence() {
  const [asset, setAsset] = useState("BTC");
  const [size, setSize] = useState(1000);
  const [quotes, setQuotes] = useState([]);
  const [buy, setBuy] = useState(null);
  const [sell, setSell] = useState(null);
  const [arb, setArb] = useState([]);

  const load = useCallback(async () => {
    try {
      const [q, b, s, a] = await Promise.all([
        api.get("/venue-intelligence/quotes", { params: { assets: asset, size_usd: size } }),
        api.get("/venue-intelligence/best-execution", { params: { asset, side: "BUY", size_usd: size } }),
        api.get("/venue-intelligence/best-execution", { params: { asset, side: "SELL", size_usd: size } }),
        api.get("/venue-intelligence/arbitrage", { params: { assets: ASSETS.join(","), size_usd: size } }),
      ]);
      setQuotes(q.data.quotes[`${asset}/USD`] || []);
      setBuy(b.data); setSell(s.data); setArb(a.data.results || []);
    } catch { /* keep prior data on transient failure */ }
  }, [asset, size]);

  useEffect(() => { load(); }, [load]);

  const evalOf = (res, venueId) => (res?.venues || []).find((v) => v.venue_id === venueId)?.eval;
  const excludedOf = (res, venueId) => (res?.excluded || []).find((e) => e.venue_id === venueId)?.reason;

  return (
    <div className="space-y-5" data-testid="venue-intelligence-page">
      <div>
        <h2 className="font-display text-xl font-bold" data-testid="venue-intelligence-title">Venue Intelligence</h2>
        <p className="text-xs text-slate-500 font-mono mt-1 max-w-3xl" data-testid="venue-readonly-note">
          Read-only best-execution and arbitrage observation across approved venues. Compares executable
          bid/ask — never last-trade price — after fees, slippage, liquidity and venue risk. This layer
          recommends venues; it cannot authorize, route, or execute capital.
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <select value={asset} onChange={(e) => setAsset(e.target.value)} data-testid="asset-selector"
          className="bg-[#111318] border border-slate-700 text-slate-100 px-3 py-1.5 rounded-sm font-mono text-xs outline-none focus:border-[#D4AF37]">
          {ASSETS.map((a) => <option key={a} value={a}>{a}/USD</option>)}
        </select>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-mono text-slate-500">ORDER SIZE $</span>
          <input value={size} onChange={(e) => setSize(Number(e.target.value) || 0)} type="number" min="1"
            data-testid="size-input"
            className="bg-[#111318] border border-slate-700 text-slate-100 px-3 py-1.5 rounded-sm font-mono text-xs outline-none focus:border-[#D4AF37] w-28" />
        </div>
      </div>

      <Panel title={`Executable Quotes — ${asset}/USD`} testId="venue-quotes-panel"
        right={<span className="text-[10px] font-mono text-slate-500">{quotes.length} venue feed(s)</span>}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono" data-testid="venue-quotes-table">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-800">
                <th className="py-2 pr-3">Venue</th><th className="pr-3">Bid</th><th className="pr-3">Ask</th>
                <th className="pr-3">Effective Buy</th><th className="pr-3">Effective Sell</th>
                <th className="pr-3">Fee</th><th className="pr-3">Slippage</th><th className="pr-3">Quote Age</th>
                <th className="pr-3">Liquidity</th><th className="pr-3">Risk</th><th></th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => {
                const be = evalOf(buy, q.venue_id), se = evalOf(sell, q.venue_id);
                const ex = excludedOf(buy, q.venue_id) || excludedOf(sell, q.venue_id);
                return (
                  <tr key={q.venue_id} className="border-b border-slate-800/50" data-testid={`venue-row-${q.venue_id.replace(/_/g, "-")}`}>
                    <td className="py-2 pr-3 text-slate-200">{q.label}
                      <span className={`ml-2 ${q.venue_health === "UP" ? "text-emerald-400" : "text-red-400"}`}>{q.venue_health}</span></td>
                    <td className="pr-3 text-slate-300">{q.bid ? usd(q.bid) : "—"}</td>
                    <td className="pr-3 text-slate-300">{q.ask ? usd(q.ask) : "—"}</td>
                    <td className="pr-3 text-slate-100">{be ? usd(be.unit_cost) : "—"}</td>
                    <td className="pr-3 text-slate-100">{se ? usd(se.unit_proceeds) : "—"}</td>
                    <td className="pr-3 text-slate-400">{q.trading_fee_bps} bps</td>
                    <td className="pr-3 text-slate-400">{be ? `${fmt(be.slippage_bps, 1)} bps` : "—"}</td>
                    <td className="pr-3 text-slate-400">{q.quote_age_ms !== undefined ? `${fmt(q.quote_age_ms, 0)} ms` : "—"}</td>
                    <td className="pr-3 text-slate-400">{q.liquidity_score ?? "—"}</td>
                    <td className="pr-3 text-slate-400">{q.counterparty_risk}</td>
                    <td className="space-x-1.5 whitespace-nowrap">
                      {buy?.best === q.venue_id && !buy?.insufficient_data && (
                        <span className="text-[9px] px-1.5 py-0.5 border rounded-sm text-emerald-400 border-emerald-800" data-testid="best-buy-badge">BEST BUY</span>)}
                      {sell?.best === q.venue_id && !sell?.insufficient_data && (
                        <span className="text-[9px] px-1.5 py-0.5 border rounded-sm text-[#D4AF37] border-[#D4AF37]/50" data-testid="best-sell-badge">BEST SELL</span>)}
                      {ex && <span className="text-[9px] px-1.5 py-0.5 border rounded-sm text-red-400 border-red-900" data-testid="venue-excluded-badge">EXCLUDED: {ex}</span>}
                    </td>
                  </tr>
                );
              })}
              {quotes.length === 0 && (
                <tr><td colSpan="11" className="py-4 text-slate-500" data-testid="venue-quotes-empty">No venue feeds available.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Best Execution" testId="best-execution-panel"
        right={buy?.confidence !== null && buy?.confidence !== undefined
          ? <span className="text-[10px] font-mono text-slate-500" data-testid="best-execution-confidence">CONFIDENCE {buy.confidence}%</span> : null}>
        {buy?.insufficient_data ? (
          <p className="text-xs text-slate-500 font-mono" data-testid="best-execution-insufficient">{buy.explanation}</p>
        ) : buy?.best ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-300 font-mono leading-relaxed" data-testid="best-execution-explanation">{buy.explanation}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
              <div><div className="metric-label">Raw spread</div><div className="text-slate-200 mt-0.5" data-testid="best-execution-raw-spread">{fmt(buy.raw_spread_bps, 1)} bps</div></div>
              <div><div className="metric-label">Fee-adjusted</div><div className="text-slate-200 mt-0.5" data-testid="best-execution-fee-spread">{fmt(buy.fee_adjusted_spread_bps, 1)} bps</div></div>
              <div><div className="metric-label">Slippage-adjusted</div><div className="text-slate-200 mt-0.5" data-testid="best-execution-slip-spread">{fmt(buy.slippage_adjusted_spread_bps, 1)} bps</div></div>
              <div><div className="metric-label">Net advantage</div><div className="text-emerald-400 mt-0.5" data-testid="best-execution-net-advantage">{usd(buy.net_advantage_usd)} ({fmt(buy.net_advantage_bps, 1)} bps)</div></div>
            </div>
          </div>
        ) : <p className="text-xs text-slate-500 font-mono" data-testid="best-execution-empty">{buy?.explanation || "Loading…"}</p>}
      </Panel>

      <Panel title="Arbitrage Watch — Observation Only" testId="arbitrage-watch-panel"
        right={<span className="text-[10px] font-mono text-slate-500">no orders · no transfers · no rebalancing</span>}>
        {arb.every((r) => r.state === "NONE" && !r.buy_venue) ? (
          <p className="text-xs text-slate-500 font-mono" data-testid="arbitrage-empty">
            {arb[0]?.reason || "Fewer than two valid venue feeds — cross-venue comparison unavailable."}
          </p>
        ) : (
          <div className="space-y-2">
            {arb.map((r) => (
              <div key={r.asset} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-mono border-b border-slate-800/50 pb-2"
                data-testid={`arbitrage-row-${r.asset.replace("/", "-").toLowerCase()}`}>
                <span className="text-slate-200 w-20">{r.asset}</span>
                <span className={`text-[10px] px-1.5 py-0.5 border rounded-sm ${ARB_CLS[r.state]}`}
                  data-testid={`arbitrage-state-${r.asset.replace("/", "-").toLowerCase()}`}>{r.state}</span>
                {r.net_edge_bps !== null && r.net_edge_bps !== undefined && (
                  <span className="text-slate-300">edge {fmt(r.net_edge_bps, 1)} bps · {usd(r.net_edge_usd)}</span>)}
                {r.buy_venue && <span className="text-slate-500">buy {r.buy_venue} → sell {r.sell_venue}</span>}
                <span className="text-slate-600">{r.reason}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
